import React, { useState, useEffect, useRef } from 'react';

import { CommentStatus, CommentThread } from '../../store/config/types/comment.types';
import './CommentPreview.scss';
import CommentService from '../../services/comment.service';
import { Card, CircularProgress } from '@mui/material';
import { Comment } from '@material-ui/icons';
import { CSSProperties } from '@material-ui/core/styles/withStyles';

interface CommentPreviewProps {
  entityInfo: { entity: string; entityId: number };
  badgeOnly?: boolean;
  style?: CSSProperties;
  theme?: 'dark' | 'light';
  // eslint-disable-next-line
  onClick?: (thread: CommentThread, callback?: (thread: CommentThread) => void) => void;
}

function CommentPreview({ entityInfo, badgeOnly, style, theme = 'light', onClick }: CommentPreviewProps) {
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [commentThread, setCommentThread] = useState<CommentThread>();
  const [status, setStatus] = useState<CommentStatus>();

  useEffect(() => {
    if (entityInfo?.entity && entityInfo?.entityId && !loadingRef.current) {
      loadComments();
    }
  }, [entityInfo]);
  function updateStatus(thread: CommentThread) {
    const threadStatus: CommentStatus = {
      unreadCount: 0,
    };
    thread.comments.forEach((comment) => {
      if (comment.isNew) {
        if (!threadStatus.firstUnreadComment) {
          threadStatus.firstUnreadComment = comment;
        }
        threadStatus.unreadCount++;
      }
    });
    threadStatus.lastComment = thread.comments[thread.comments.length - 1];

    setStatus(threadStatus);
  }
  const loadComments = async () => {
    setLoading(true);
    loadingRef.current = true;
    try {
      const data = await CommentService.getCommentThread(entityInfo.entity, entityInfo.entityId);
      updateStatus(data);
      setCommentThread(data);
    } catch (error) {
      console.error('Failed to load comments', error);
      // ons.notification.toast("Failed to load comments", { timeout: 2000 });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (onClick && commentThread)
      onClick(commentThread, (thread) => {
        updateStatus(thread);
        setCommentThread(thread);
      });
  }

  const hasComment = Boolean(status?.firstUnreadComment ?? status?.lastComment);
  return (
    <div style={style}>
      <Card className={`comment-preview${badgeOnly ? ' badge-only' : ''} ${theme}`} onClick={handleClick}>
        {loading ? <CircularProgress size={16} /> : <Comment className="comment-icon" />}
        {(status?.unreadCount ?? 0) > 0 && (
          <div className="unread-badge">{(status?.unreadCount ?? 0) > 99 ? '99+' : status?.unreadCount ?? 0}</div>
        )}
        {!badgeOnly && (
          <div className="preview-content">
            <span className={hasComment ? 'last-comment-text' : 'no-comments'}>
              <span className="author">
                {(status?.firstUnreadComment ?? status?.lastComment ?? { displayName: '' }).displayName}
              </span>
              {(status?.firstUnreadComment ?? status?.lastComment ?? { text: '+ Write a comment' }).text}
            </span>
          </div>
        )}
      </Card>
    </div>
  );
}

export default React.memo(CommentPreview, (prevProps, nextProps) => {
  if (prevProps.badgeOnly != nextProps.badgeOnly) return false;
  if (prevProps.entityInfo?.entity != nextProps.entityInfo?.entity) return false;
  if (prevProps.entityInfo?.entityId != nextProps.entityInfo?.entityId) return false;
  return true;
});
